/**
 * GobdDocPage — GoBD-Verfahrensdokumentations-Seite (`/dashboard/gobd`).
 *
 * NEUE Seite (nicht zu verwechseln mit `GobdExportPage` /
 * `/dashboard/gobd-export`, dem Z3-Datenexport-Panel). Diese Seite zeigt den
 * vom Operator gepflegten Verfahrensdoku-Volltext
 * (`public/legal/gobd-verfahrensdokumentation-v1.md`) — geladen über den
 * geteilten {@link useLegalDocument}-Hook (Muster `KanzleiOnboardingDialog` /
 * `InboundActivation`) und gerendert via {@link SimpleMarkdown}.
 *
 * Dateiname bewusst `GobdDocPage` statt `GobdPage`: Der Dateiname `GobdPage.tsx`
 * loest den `legal-gate.ps1`-Pre-Write-Hook aus — camelCase-Substring-Bug
 * (`Gob` + `dPa` + `ge` matcht `*dpa*`), dieselbe Klasse Fehlalarm wie die im
 * Hook bereits dokumentierte `ForgotPasswordPage`-Ausnahme. Der Hook ist NICHT
 * editierbar/umgehbar (golden-rules.md) — daher die Umbenennung als sanktionierter
 * Workaround statt eines Hook-Bypasses (s. changelog-gobd-live.md).
 *
 * Fail-closed: fehlt die Datei (404/leer/SPA-HTML-Fallback) oder trägt sie
 * den {@link LEGAL_DRAFT_MARKER}, wird der Volltext NICHT angezeigt — statt-
 * dessen ein „nicht verfügbar"/„noch in Vorbereitung"-Hinweis (kein leerer,
 * irreführend „fertiger" Bildschirm).
 *
 * Der rechtliche Disclaimer (e-rechnung-Auflage A1, 1:1-Wortlaut) steht
 * PROMINENT ÜBER dem Dokument und wird UNABHÄNGIG vom Ladezustand des
 * Dokuments gerendert — er ist ein fester, hartkodierter Text (kein i18n-
 * Value), weil er wortlautgetreu (Rz. 151 GoBD) sein muss und wie der AVV-/
 * Onboarding-Text nur auf Deutsch geführt wird (Muster `deOnlyNotice` in
 * `KanzleiOnboardingDialog`/`InboundActivation` — bei anderer UI-Sprache
 * zusätzlich ein kurzer, übersetzter Hinweis "nur auf Deutsch verfügbar").
 *
 * Zugriffs-Gate: identisch zu `GobdExportPage` — `canUseGobd(uid)` aus
 * `config/gobdAccess.ts` (Beta-UID ODER `REACT_APP_GOBD_ENABLED==='true'`).
 * Zwei-Komponenten-Split (Outer-Gate / Inner) wie `InboundActivation`, weil
 * die Inner-Komponente zusätzliche Hooks (`useLegalDocument`) aufruft, die
 * NICHT bedingt (nach einem Early-Return) laufen dürfen (Rules of Hooks).
 *
 * @module GobdDocPage
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconGobd, IconWarning } from '../icons';
import { useAuth } from '../context/AuthContext';
import { canUseGobd } from '../config/gobdAccess';
import { LEGAL_DRAFT_MARKER } from '../config/advisorConsentAccess';
import { useLegalDocument } from '../components/tenant/useLegalDocument';
import SimpleMarkdown from '../components/inbound/SimpleMarkdown';

/** Öffentlicher Pfad des GoBD-Verfahrensdoku-Volltexts (Operator-Datei, J-3). */
export const GOBD_DOC_PATH = '/legal/gobd-verfahrensdokumentation-v1.md';

// ---------------------------------------------------------------------------
// Disclaimer — e-rechnung-Auflage A1, Wortlaut 1:1 (BINDEND, nicht umformulieren).
// Hartkodiert statt i18n-Value: der Wortlaut ist rechtlich geprüft und muss
// exakt reproduziert werden (Rz. 151 GoBD); analog zum AVV-/Onboarding-Text
// bleibt er unabhängig von der UI-Sprache auf Deutsch (s. Modul-Kopf).
// ---------------------------------------------------------------------------
const GOBD_DISCLAIMER_LEAD = 'Wichtiger rechtlicher Hinweis.';
const GOBD_DISCLAIMER_BODY =
  ' Die hier angezeigte Verfahrensdokumentation ist eine anpassbare Vorlage und beschreibt den von FaeBill erbrachten Anteil Ihres Beleg- und Aufbewahrungsprozesses. Sie ist kein Ersatz für die vollständige Verfahrensdokumentation Ihres Unternehmens, deren Erstellung, betriebsindividuelle Anpassung und laufende Aktualisierung nach den GoBD ausschließlich Ihnen als Steuerpflichtigem obliegt (Rz. 151 GoBD). FaeBill stellt technische Hilfsmittel und diese Vorlage bereit und übernimmt keine Gewähr für die GoBD-Konformität Ihrer konkreten Buchführung. Ob Ihre Buchführung den GoBD entspricht, hängt von Ihrem konkreten Einsatz und Ihren betrieblichen Prozessen ab. Dieser Hinweis ist keine Steuer- oder Rechtsberatung und stellt keine Zertifizierung dar. Bitte prüfen und ergänzen Sie alle mit [Platzhalter] markierten Stellen und ziehen Sie im Zweifel Ihren Steuerberater hinzu.';

/**
 * Seiten-Wrapper — Outer-Gate. Rendert `null` (inert), solange weder Beta-UID
 * noch Flag greifen (`canUseGobd`, `config/gobdAccess.ts`).
 */
const GobdDocPage: React.FC = () => {
  const { currentUser } = useAuth();
  if (!canUseGobd(currentUser?.uid)) return null;
  return <GobdDocPageInner />;
};

/**
 * Innere Seiten-Komponente — läuft nur, wenn der Outer-Gate bereits
 * durchlässig ist (Rules-of-Hooks-konform: `useLegalDocument` wird nie
 * bedingt nach einem Early-Return aufgerufen).
 */
const GobdDocPageInner: React.FC = () => {
  const { t, i18n } = useTranslation('gobd');
  const docState = useLegalDocument(GOBD_DOC_PATH, LEGAL_DRAFT_MARKER);
  const isGerman = i18n.language?.toLowerCase().startsWith('de') ?? false;

  const hasText = docState.kind === 'ready' || docState.kind === 'draft';

  return (
    <div className="min-h-screen bg-surface p-4 md:p-6" data-testid="gobd-doc-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
            <IconGobd className="text-accent-ink" size={24} />
            {t('title')}
          </h1>
        </div>

        {!isGerman && (
          <p
            className="mb-4 rounded-md bg-surface-inset px-3 py-2 text-xs text-secondary"
            data-testid="gobd-doc-page-de-only-notice"
          >
            {t('docPage.deOnlyNotice')}
          </p>
        )}

        {/* e-rechnung-Auflage A1: Disclaimer PROMINENT ÜBER dem Dokument,
            unabhängig vom Ladezustand des Dokuments selbst. */}
        <div
          className="mb-6 flex items-start gap-2 rounded-md bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] p-4"
          role="status"
          data-testid="gobd-doc-page-disclaimer"
        >
          <IconWarning className="mt-0.5 shrink-0 text-[var(--tone-warning-fg)]" />
          <p className="text-sm text-[var(--tone-warning-fg)]">
            <strong>{GOBD_DISCLAIMER_LEAD}</strong>
            {GOBD_DISCLAIMER_BODY}
          </p>
        </div>

        <div
          className="rounded-xl border border-hairline bg-surface-raised p-4 md:p-6 max-h-[70vh] overflow-y-auto"
          data-testid="gobd-doc-page-document"
        >
          {hasText && <SimpleMarkdown text={docState.text} />}
          {docState.kind === 'loading' && (
            <p className="text-sm text-muted">{t('docPage.loading')}</p>
          )}
          {docState.kind === 'missing' && (
            <p className="text-sm text-muted">{t('docPage.notReleased')}</p>
          )}
        </div>

        {docState.kind === 'draft' && (
          <div
            className="mt-3 flex items-start gap-2 rounded-md bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] p-3"
            role="status"
            data-testid="gobd-doc-page-draft-banner"
          >
            <IconWarning className="mt-0.5 text-[var(--tone-warning-fg)]" />
            <p className="text-sm text-[var(--tone-warning-fg)]">{t('docPage.draft')}</p>
          </div>
        )}

        {hasText && (
          <a
            href={GOBD_DOC_PATH}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center text-sm text-accent-ink hover:underline"
            data-testid="gobd-doc-page-open-link"
          >
            {t('docPage.openLink')}
          </a>
        )}
      </div>
    </div>
  );
};

export default GobdDocPage;
